import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import useGroupParticipants from "./groupParticipants";
import {
  buildParticipantIndex,
  describeMention,
  mentionTokensOf,
  mentionedIdsOf,
  normalizeParticipant,
} from "./mentions";

// Se o servidor ainda não tem a rota de menções (404), não adianta perguntar de novo.
let mentionsRouteMissing = false;

// Resolve "@<id>" -> "@Nome" nas mensagens de um ticket de grupo.
const useGroupMentions = ({ enabled, contactId, ticketId, messages }) => {
  const { participants, loaded } = useGroupParticipants(enabled ? contactId : null);
  const [extra, setExtra] = useState({}); // dígitos -> participante vindo da consulta ao servidor
  const askedRef = useRef(new Set());

  useEffect(() => {
    askedRef.current = new Set();
    setExtra({});
  }, [ticketId]);

  const index = useMemo(() => buildParticipantIndex(participants), [participants]);

  // Quem foi marcado e não está na lista do grupo (saiu, ou a lista não carregou): pergunta ao
  // servidor uma única vez por id.
  useEffect(() => {
    if (!enabled || !ticketId || !loaded || mentionsRouteMissing) return;
    const unknown = [];
    const consider = (digits) => {
      if (index.has(digits) || askedRef.current.has(digits)) return;
      askedRef.current.add(digits);
      unknown.push(digits);
    };
    messages.forEach((m) => {
      if (!m || m.pending) return;
      const declared = mentionedIdsOf(m);
      mentionTokensOf(m.body).forEach((digits) => {
        if ((declared && declared.has(digits)) || digits.length >= 14) consider(digits);
      });
      if (m.quotedMsg) {
        mentionTokensOf(m.quotedMsg.body).forEach((digits) => {
          if (digits.length >= 14) consider(digits);
        });
      }
    });
    if (unknown.length === 0) return;
    api
      .get(`/tickets/${ticketId}/mentions`, { params: { ids: unknown.join(",") } })
      .then(({ data }) => {
        if (!Array.isArray(data)) return;
        const found = {};
        data.forEach((row) => {
          const p = normalizeParticipant(row);
          if (!p || (!p.name && !p.number)) return;
          [p.user, p.lid, p.number].filter(Boolean).forEach((key) => {
            found[key] = p;
          });
        });
        if (Object.keys(found).length > 0) setExtra((prev) => ({ ...prev, ...found }));
      })
      .catch((err) => {
        if (err && err.response && err.response.status === 404) mentionsRouteMissing = true;
      });
  }, [enabled, ticketId, loaded, index, messages]);

  // (dígitos, mensagem) -> { label, title } ou null se não for menção
  const resolveMention = useCallback(
    (digits, message) => {
      if (!enabled) return null;
      return describeMention(digits, {
        participant: index.get(digits) || extra[digits] || null,
        mentioned: mentionedIdsOf(message),
        fallbackLabel: i18n.t("conversation.mentions.participant"),
      });
    },
    [enabled, index, extra]
  );

  return { resolveMention, participants };
};

export default useGroupMentions;
